<!--赖传峰-->
<!--2022/3/30-->
<script lang="ts" setup>
import {
  computed,
  defineAsyncComponent,
  defineI18nRoute,
  definePageMeta,
  navigateTo,
  onMounted,
  provide,
  ref,
  toRefs,
  useCookie,
  useFetch,
  useHead,
  useI18n,
  useNuxtApp,
  useRequestHeaders,
  useRoute,
  useRouter,
  useSeoMeta,
  watchEffect,
} from '#imports'
import { type GetShareQuery, ThumbnailSize } from '@/api'
import ogNoneThumbnailImage from '@/assets/images/share/og-none-thumbnail-image.png'
import { isVideoOrImage } from '@/components/share/fileExplorer.script'
import FileSpaceNotEnough from '@/components/share/FileSpaceNotEnough.vue'
import { isFolder, shareTrackParams } from '@/components/share/share.script'
import useDomain from '@/composables/useDomain'
import useParentId from '@/composables/useParentId'
import { useShareAction } from '@/composables/useShareAction'
import useShareId from '@/composables/useShareId'
import { useLayout } from '@/store/layout'
import { RestoreResultStatus, type ShareState, useShare } from '@/store/share'
import {
  getOrderTypeFromStorage,
  isNaturalSort,
  naturalSort,
  paramOrder,
  removeUrlKeyFromFiles,
} from '@/store/share.script'
import $track from '@/track/webShare'
import { DriveKind, FileOrder, InjectionKey, RouteQueryKey, ShareStatus, StorageKey } from '@/types/enum'
import apiPrefix from '@/utils/apiPrefix'
import clearQueryByParams from '@/utils/clearQueryByParams'
import ssrReqHeader from '@/utils/ssrReqHeader'
import { hydrateOnVisible } from 'vue'
import FileExplorer from '~/components/share/FileExplorer.vue'
import useDisplayMode from '~/composables/useDisplayMode'
import usePageTitle from '~/composables/usePageTitle'
import useShareFiles from '~/composables/useShareFiles'
import { ipBlock } from '~/middleware/ipBlock.global'
import { BOT_REGEX } from '~/types/constants'
import isMobileInServerAndClient from '~/utils/isMobileInServerAndClient'

// 异步组件定义：使用 defineAsyncComponent 延迟加载组件，减少初始页面加载时间
// 恢复结果组件：显示文件恢复/保存到自己网盘的结果
const AsyncRestoreResult = defineAsyncComponent({
  loader: () => import('@/components/share/RestoreResult.vue'),
  hydrate: hydrateOnVisible(), // 当组件进入视口时才进行水合（hydration）
})
// 需要密码组件：处理分享链接需要密码的情况
const AsyncNeedPasscode = defineAsyncComponent({
  loader: () => import('@/components/share/NeedPasscode.vue'),
  hydrate: hydrateOnVisible(),
})
// 分享不可用组件：处理分享链接已失效或其他错误状态
const AsyncShareNotAvailable = defineAsyncComponent({
  loader: () => import('@/components/share/ShareNotAvailable.vue'),
  hydrate: hydrateOnVisible(),
})
// 活动顶部栏组件：显示促销活动信息
const AsyncActivityTopBar = defineAsyncComponent(() => import('@/components/activity/ActivityTopBar.vue'))

// 是否显示文件的缩略图与文件名，如果是搜索引擎或者爬虫，默认值为 true，否则为 false
// 通过检测 user-agent 判断访问者是否为搜索引擎爬虫
const isRobot = ref(BOT_REGEX.test(useRequestHeaders(['user-agent'])['user-agent'] || ''))

// 获取API前缀，用于构建API请求URL
const { PP_DRIVE_API } = apiPrefix(useDomain().value)
// 内部API地址，开发环境使用外部API，生产环境使用内部微服务地址
const PP_DRIVE_API_INTERNAL = process.dev ? PP_DRIVE_API : 'http://drive:80'
// 获取分享状态管理器
const shareStore = useShare()
// 从Nuxt应用实例获取密码令牌相关方法
const {
  $getPasscodeToken, // 获取分享密码令牌
  $clearPasscodeToken, // 清除分享密码令牌
  $setPasscodeToken, // 设置分享密码令牌
} = useNuxtApp()
const { t } = useI18n() // 国际化翻译函数

// 获取当前URL中的parentId（如果在子文件夹中）
const parentId = useParentId()
// 获取当前分享ID
const shareId = useShareId()
const router = useRouter()
const route = useRoute()

// 从布局存储中获取是否为移动设备的响应式引用
const { isMobile } = toRefs(useLayout())
// 从URL查询参数中获取分享密码
const passCode = route.query[RouteQueryKey.SHARE_PASSCODE] as string

// 获取服务端请求头
const headers = ssrReqHeader()
// 获取文件排序方式（从存储中读取用户偏好）
const orderType = await getOrderTypeFromStorage(true)
// 获取是否将文件夹置顶的显示偏好
const { pinFoldersTop } = useDisplayMode()

// 构建分享API请求参数
const shareQuery: GetShareQuery = {
  share_id: shareId.value, // 分享ID
  pass_code: passCode, // 分享密码（如有）
  pass_code_token: $getPasscodeToken().value, // 分享密码令牌（如有）
  thumbnail_size: isMobileInServerAndClient()
    ? ThumbnailSize.SIZE_SMALL // 移动设备使用小缩略图
    : ThumbnailSize.SIZE_LARGE, // 桌面设备使用大缩略图
  limit: 1e2, // 每页显示文件数量限制为100
  folders_first: pinFoldersTop.value, // 是否将文件夹排在前面
  order: paramOrder(orderType), // 文件排序方式
}

// ===== 文件列表加载逻辑开始 =====
// 在服务端渲染时获取分享数据，客户端渲染时不执行此逻辑
// 请求错误的时候为 NULL
const shareResData = import.meta.server
  ? (
      await useFetch<Share>(`${PP_DRIVE_API_INTERNAL}/drive/v1/share`, {
        query: shareQuery,
        headers,
        transform: removeUrlKeyFromFiles, // 移除文件URL中的敏感key参数
      })
    ).data.value
  : null

// 服务端渲染时，如果成功获取分享数据，处理密码令牌和自动跳转等逻辑
if (import.meta.server && shareResData) {
  /* 如果有返回 pass_code_token, 再次设置，延长有效期 */
  shareResData.pass_code_token && $setPasscodeToken(shareResData.pass_code_token)
  // 获取分享密码（优先使用URL参数，其次使用Cookie）
  const code = passCode || useCookie(`${StorageKey.SHARE_PWD_CODE}_${shareId.value}`).value
  // 如果有密码，将其保存到Cookie中，有效期7天
  code && (useCookie(`${StorageKey.SHARE_PWD_CODE}_${shareId.value}`, { maxAge: 7 * 24 * 60 * 60 }).value = code)
  // 获取URL中的action参数
  const queryAction = useShareAction()

  // url 的参数优先级更高
  if (queryAction) shareResData.params = queryAction

  // 配置了自动跳转二级目录
  const { params, files } = shareResData
  // 检查是否需要自动进入子目录（根据分享链接参数）
  const canEnterSubDir = params && (params.auto_enter_subdir === 'true' || params.auto_play === 'true')
  if (isRobot.value) {
    //  如果是搜索引擎爬虫，直接返回，不进行自动跳转
  } else if (canEnterSubDir && files.length === 1 && isFolder(files[0].kind) && !parentId.value) {
    // 自动导航到子文件夹
    navigateTo({
      path: `/s/${shareId.value}/${files[0].id}`,
      query: route.query,
    })
  } else if (queryAction) {
    // 设置URL查询参数到store
    shareStore.urlQuery = queryAction
  }
}

// 如果在子文件夹中，需要获取子文件夹的详细信息
const shareDetailResData =
  import.meta.server && parentId.value && shareResData?.share_status === ShareStatus.OK
    ? (
        await useFetch<ShareState['detail'][string]>(`${PP_DRIVE_API_INTERNAL}/drive/v1/share/detail`, {
          query: {
            ...shareQuery,
            parent_id: parentId.value, // 子文件夹ID
            pass_code_token: shareResData?.pass_code_token, // 使用之前获取的密码令牌
          },
          headers,
          transform: input => (input ? removeUrlKeyFromFiles(input) : input), // 移除文件URL中的敏感key参数
        })
      ).data.value
    : null
// ===== 文件列表加载逻辑结束 =====

// 计算属性：获取当前分享数据
const share = computed<Share>(() => {
  // 客户端渲染时直接从store中获取数据
  if (import.meta.client) return shareStore.data

  if (shareResData) {
    // 服务端渲染时，如果是自然语言排序，需要手动对文件进行排序
    if (isNaturalSort(orderType) && shareResData.files) {
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      shareResData.files = naturalSort(shareResData.files, orderType as FileOrder.NAME_ASC)
    }
    // 将获取的数据保存到store中
    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    shareStore.data = shareResData
    return shareResData
  }

  // 如果没有获取到数据，返回store中的数据
  return shareStore.data
})

// 计算是否需要显示密码输入界面的CSS类
const isNeedPasscodeClass = computed(() => !isMobile.value && share.value.share_status === ShareStatus.PASS_CODE_EMPTY)

// 计算属性：获取子文件夹的详细信息
const shareDetail = computed<ShareState['detail'][string]>(() => {
  // 如果不在子文件夹中，不需要详细信息
  if (!parentId.value) return null
  // 客户端渲染时从store中获取数据
  if (import.meta.client) return shareStore.detail[parentId.value]

  // 服务端渲染时，如果是自然语言排序，需要手动对文件进行排序
  if (isNaturalSort(orderType) && shareDetailResData?.files) {
    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    shareDetailResData.files = naturalSort(shareDetailResData.files, orderType as FileOrder.NAME_ASC)
  }
  // 将获取的数据保存到store中
  // eslint-disable-next-line vue/no-side-effects-in-computed-properties
  shareStore.detail[parentId.value] = shareDetailResData
  return shareDetailResData
})

// 将分享数据和子文件夹详细信息提供给子组件
provide<ShareRef>(InjectionKey.SHARE, share)
provide<ShareDetailRef>(InjectionKey.SHARE_DETAIL, shareDetail)

// 获取需要渲染的文件列表，优先使用子文件夹的文件列表，其次使用分享根目录的文件列表
const files4Render = useShareFiles({ share, shareDetail })
// 判断是否为单个文件的分享模式（用于特殊UI处理）
const isSingleShareFile = computed(
  () =>
    !parentId.value &&
    files4Render.value.length === 1 &&
    files4Render.value[0].kind === DriveKind.FILE &&
    (isMobile.value ? true : isVideoOrImage(files4Render.value[0].file_extension, files4Render.value[0].mime_type)),
)

// 生成页面元数据（标题、描述、Open Graph标签等）
const htmlMetaData = computed<{
  ogTitle: string
  ogDescription?: string
  ogImage?: string
  pageTitle: string
  pageDescription: string
}>(() => {
  const titlePrefix = t('share-author', [share.value.user_info?.nickname])
  const pageTitle = usePageTitle(titlePrefix).value

  if (share.value.share_status === ShareStatus.OK) {
    // 合并文件名（最多10个）作为OG标题
    // 找出文件夹，并按文件顺序排列，一般各5个
    const folderList = [...files4Render.value].filter(el => el.kind === DriveKind.FOLDER).slice(0, 5)
    const fileList = [...files4Render.value].filter(el => el.kind !== DriveKind.FOLDER)
    // 按文件大小排序，最大的文件排在最前面
    fileList.sort((aFile, bFile) => {
      const aSize = Number(aFile.size) || 0
      const bSize = Number(bFile.size) || 0
      return bSize - aSize
    })
    // 将文件夹插入到文件列表的第5个位置
    fileList.splice(5, 0, ...folderList)

    // 优先展示文件，其次展示文件夹 ， 因为文件可以优先按大小排序
    const mergeList = fileList

    const mergedName = mergeList
      .slice(0, 10)
      .map(el => el.name)
      .join(' ')

    const ogTitle = t('share-og-title', [mergedName]) // 翻译OG标题

    return {
      ogTitle,
      ogDescription: pageTitle,
      // 使用第一个有缩略图的文件作为OG图片，没有则使用默认图片
      ogImage: mergeList.map(el => el.thumbnail_link).find(el => el) || ogNoneThumbnailImage,
      pageTitle,
      pageDescription: pageTitle,
    }
  }

  return {
    ogTitle: pageTitle,
    ogDescription: pageTitle,
    ogImage: ogNoneThumbnailImage,
    pageTitle,
    pageDescription: pageTitle,
  }
})
// 设置SEO元数据
useSeoMeta({
  title: htmlMetaData.value.pageTitle,
  description: htmlMetaData.value.pageDescription,
  ogType: 'video.movie',
  ogDescription: htmlMetaData.value.pageDescription,
  ogTitle: htmlMetaData.value.ogTitle,
  ogImage: htmlMetaData.value.ogImage ? { url: htmlMetaData.value.ogImage } : undefined,
})
// Google 编入索引优化：添加规范链接标签
useHead({
  link: [
    {
      rel: 'canonical',
      href: `https://${useDomain().value}${route.path}`,
    },
  ],
})

// 判断分享是否处于可操作状态（OK、需要密码、密码错误）
const hasOperationStatus = computed(() =>
  [ShareStatus.OK, ShareStatus.PASS_CODE_EMPTY, ShareStatus.PASS_CODE_ERROR].includes(share.value.share_status),
)

onMounted(() => {
  /* 当前的分享链接不可用时，清除当前链接保存的密码 */
  if (!share.value || share.value.share_status !== ShareStatus.OK) {
    $clearPasscodeToken()
    useCookie(`${StorageKey.SHARE_PWD_CODE}_${shareId.value}`, { maxAge: 0 }).value = null
  }
  /* share 的状态是 OK 但 shareDetail 又不可用时，判定 parentId 无效，替换 URL，只保留 share_id */
  if (shareStore.data.share_status === ShareStatus.OK && parentId.value && !shareStore.detail[parentId.value]) {
    router.replace({
      params: {
        share_id: shareId.value,
        parent_ids: undefined,
      },
    })
  }

  // 清除URL中的action参数
  clearQueryByParams(['act'])
})

// 页面访问统计跟踪
watchEffect(() => {
  if (import.meta.server) return
  if (!useCookie(StorageKey.ACCESS_TO_VISIT).value) return
  switch (share.value.share_status) {
    case ShareStatus.OK:
      return $track.web_share_page_show(shareTrackParams())
    case ShareStatus.PASS_CODE_EMPTY:
    case ShareStatus.PASS_CODE_ERROR:
      return $track.web_share_password_page_show()
    default:
      $track.web_share_abnormal_page_show({ error: share.value.share_status })
  }
})

onMounted(() => {
  // 强制进行 IP 检测，决定是否显示文件信息
  // 通过 IP 检测后，认为并非机器人
  ipBlock({ ignoreCache: true }).then(val => (isRobot.value = val === undefined))
})

// 设置页面元数据
definePageMeta({
  layout: 'drive',
  middleware: 'share',
})
// 禁用i18n路由自动生成
false && /*#__PURE__*/ defineI18nRoute(false)
</script>

<template>
  <div
    :class="{ 'need-passcode': isNeedPasscodeClass, 'single-file': isSingleShareFile }"
    class="share"
  >
    <div class="share-container">
      <!-- 移动端恢复结果展示 -->
      <AsyncRestoreResult v-if="isMobile && shareStore.restoreResult.status !== RestoreResultStatus.UNKNOWN" />
      <template v-else-if="hasOperationStatus">
        <template v-if="share.share_status === ShareStatus.OK">
          <!-- 活动顶部栏，仅客户端渲染 -->
          <ClientOnly>
            <AsyncActivityTopBar scene="share_page" />
          </ClientOnly>
          <!-- 文件浏览器组件，这里是实际显示文件列表的核心组件 -->
          <FileExplorer :show-file-info="isRobot" />
        </template>
        <!-- 需要密码的界面 -->
        <AsyncNeedPasscode v-else />
      </template>
      <!-- 分享不可用提示 -->
      <AsyncShareNotAvailable
        v-else
        :status="share.share_status"
        :status-text="share.share_status_text"
      />
      <!-- 存储空间不足提示，仅客户端渲染 -->
      <ClientOnly>
        <FileSpaceNotEnough />
      </ClientOnly>
    </div>
  </div>
</template>

<style lang="less">
@import '@/assets/styles/util.less';

.share {
  height: 100%;

  &-container {
    display: flex;
    flex-direction: column;
    height: 100%;

    .activity-top-bar {
      flex: 0 0 auto;
    }

    .file-explorer:not(.single-file) {
      flex: 1 1 auto;
      height: 0 !important;
    }
  }

  @media screen and (min-width: @mobile-break-width) {
    padding-top: 12px;
    background: #f5f6fa;

    &.single-file {
      padding-bottom: 24px;
    }

    &-container {
      #public-page-content();
    }
  }

  &.need-passcode {
    background: url('@/assets/images/share/passcode-bg.jpeg') var(--no-repeat-center) / cover #e1f1fe;
  }
}
</style>

<style>
.need-passcode .share {
  background: transparent;
}
</style>
